import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getCompany, updateCompany, updateCompanyMeta, createAdminUser,
  toggleAdminUser, resetAdminPassword, resetAdminMfa, getCompanyActivity, getCompanyAuditLogs, CompanyAuditLog,
  listCompanyUsers, purgeUser, getMyCompanyAccess, accessCompanyAsSuperAdmin, openCompanyAdminConsole, openCompanyCrewApp,
  listAssets, Asset,
  buildCompanyApk, getApkBuildStatus, ApkBuildStatus,
  listOfflineTerminals, createOfflineTerminal, deleteOfflineTerminal, OfflineTerminal,
  uploadCompanyLogo, deleteCompanyLogo,
  CompanyDetail, DailyCount, CompanyUser,
} from '../services/api';
import { fmtDateJst, fmtDateTimeJst } from '../utils/datetime';
import PasswordInput from '../components/PasswordInput';
import QRCode from 'qrcode';

function MiniBarChart({ data }: { data: DailyCount[] }) {
  const max = Math.max(...data.map(d => d.count), 1);
  const W = 700; const H = 80;
  const bw = W / data.length;
  const showEvery = Math.max(1, Math.ceil(data.length / 10));
  return (
    <svg viewBox={`0 0 ${W} ${H + 20}`} style={{ width: '100%', display: 'block' }}>
      {data.map((d, i) => {
        const bh = Math.max(1, Math.round((d.count / max) * H));
        return (
          <g key={i}>
            <rect x={i * bw + 1} y={H - (d.count > 0 ? bh : 0)} width={bw - 2} height={d.count > 0 ? bh : 1}
              fill={d.count > 0 ? '#3b82f6' : '#f1f5f9'} rx={2}>
              <title>{`${d.date}: ${d.count}件`}</title>
            </rect>
            {i % showEvery === 0 && (
              <text x={i * bw + bw / 2} y={H + 14} textAnchor="middle" fontSize="8" fill="#94a3b8">{d.date.slice(5)}</text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

const ADMIN_DOMAIN = 'unison-sea.com';
const APK_BASE     = 'https://dl.unison-sea.com/apk';

type Admin = CompanyDetail['admins'][number];

interface AddForm {
  user_name: string; employee_code: string; email: string; password: string;
}
interface PwModal { admin: Admin; value: string; }

interface MetaForm { memo: string; sales_contact: string; contract_date: string; renewal_date: string; website_url: string; }

export default function CompanyDetailPage({ saRole }: { saRole?: string }) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [company, setCompany]   = useState<CompanyDetail | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [lentAssets, setLentAssets] = useState<Asset[]>([]);

  // 共有端末トークン（オフライン打刻端末の認証）
  const [terminals, setTerminals] = useState<OfflineTerminal[]>([]);
  const [showPersonalQr, setShowPersonalQr] = useState(false);
  const [personalQr, setPersonalQr] = useState<string | null>(null);
  useEffect(() => {
    if (!showPersonalQr || !company) return;
    // 保存して配れるよう大きめに作る（表示は240px）。iPhone・Android 共通のURL
    QRCode.toDataURL(`https://${company.subdomain}.crew.${ADMIN_DOMAIN}/?app=personal`, { width: 720, margin: 1 })
      .then(setPersonalQr).catch(() => setPersonalQr(null));
  }, [showPersonalQr, company]);
  const [termLabel, setTermLabel] = useState('');
  const [issuedToken, setIssuedToken] = useState<{ token: string; label: string } | null>(null);
  const [termBusy, setTermBusy] = useState(false);
  const reloadTerminals = () => { if (id) listOfflineTerminals(id).then(setTerminals).catch(() => {}); };
  const issueTerminal = async () => {
    if (!id || termBusy) return;
    setTermBusy(true);
    try {
      const r = await createOfflineTerminal(id, termLabel.trim() || '共有端末');
      setIssuedToken({ token: r.token, label: termLabel.trim() || '共有端末' });
      setTermLabel('');
      reloadTerminals();
    } catch (e) { alert(e instanceof Error ? e.message : '発行に失敗しました'); }
    finally { setTermBusy(false); }
  };
  const revokeTerminal = async (t: OfflineTerminal) => {
    if (!window.confirm(`端末トークン「${t.label ?? t.id}」を失効します。この端末はオフライン名簿・顔認証ログインが使えなくなります。よろしいですか？`)) return;
    try { await deleteOfflineTerminal(t.id); reloadTerminals(); } catch { /* ignore */ }
  };

  // 船員APK ビルド
  const [apkBuild, setApkBuild] = useState<ApkBuildStatus | null>(null);
  const startApkBuild = async () => {
    if (!id) return;
    if (!window.confirm('この会社の船員APKをビルドします。\n受け皿（ドメイン/証明書）の整備も自動で行い、数分かかります。実行しますか？')) return;
    setApkBuild({ status: 'running', log: ['ビルドを開始しています...'] });
    try {
      await buildCompanyApk(id);
      const poll = setInterval(async () => {
        try {
          const s = await getApkBuildStatus(id);
          setApkBuild(s);
          if (s.status === 'success' || s.status === 'error') clearInterval(poll);
        } catch { /* 一時的な取得失敗は無視 */ }
      }, 3000);
    } catch (e) {
      setApkBuild({ status: 'error', log: [e instanceof Error ? e.message : 'ビルド開始に失敗しました'] });
    }
  };

  // 会社ロゴのアップロード/削除（管理サイト・船員アプリのログイン画面/ヘッダーに反映）
  const [logoBusy, setLogoBusy] = useState(false);
  const handleLogoUpload = async (file: File) => {
    if (!company) return;
    setLogoBusy(true);
    try {
      const url = await uploadCompanyLogo(company.id, file);
      setCompany(prev => prev ? { ...prev, logo_url: url } : prev);
    } catch (e) { alert((e as Error).message); }
    setLogoBusy(false);
  };
  const handleLogoDelete = async () => {
    if (!company) return;
    if (!window.confirm('会社ロゴを削除しますか？（既定のロゴ表示に戻ります）')) return;
    setLogoBusy(true);
    try {
      await deleteCompanyLogo(company.id);
      setCompany(prev => prev ? { ...prev, logo_url: null } : prev);
    } catch (e) { alert((e as Error).message); }
    setLogoBusy(false);
  };

  // 会社情報編集
  const [editing, setEditing]       = useState(false);
  const [editName, setEditName]     = useState('');
  const [editSub, setEditSub]       = useState('');
  const [saving, setSaving]         = useState(false);
  const [saveError, setSaveError]   = useState('');

  // 管理者追加フォーム
  const [showAdd, setShowAdd]       = useState(false);
  const [addForm, setAddForm]       = useState<AddForm>({ user_name: '', employee_code: '', email: '', password: '' });
  const [adding, setAdding]         = useState(false);
  const [addError, setAddError]     = useState('');

  // 管理者 有効/無効
  const [toggling, setToggling]     = useState<string | null>(null);

  // パスワードリセットモーダル
  const [pwModal, setPwModal]       = useState<PwModal | null>(null);
  const [pwSaving, setPwSaving]     = useState(false);
  const [pwError, setPwError]       = useState('');
  const [mfaResetting, setMfaResetting] = useState<string | null>(null);

  // ── MFAリセット（認証アプリ紛失時の救済） ──
  const handleMfaReset = async (admin: Admin) => {
    if (!id) return;
    if (!window.confirm(`${admin.user_name} の二段階認証(MFA)をリセットします。\n認証アプリを紛失した場合の救済用です。次回はMFA無しでログインでき、再設定できます。実行しますか？`)) return;
    setMfaResetting(admin.id);
    try {
      await resetAdminMfa(id, admin.id);
      setCompany(prev => prev ? { ...prev, admins: prev.admins.map(x => x.id === admin.id ? { ...x, mfa_enabled: 0 } : x) } : prev);
      alert('MFAをリセットしました。本人が次回ログイン後に再設定できます。');
    } catch (e) { alert(e instanceof Error ? e.message : 'リセットに失敗しました'); }
    finally { setMfaResetting(null); }
  };

  // 活動統計
  const [activity, setActivity]     = useState<DailyCount[]>([]);
  // 操作履歴（障害の一次切り分け: 「昨日から使えない」→ 前日に誰が何を変えたか）
  const [auditLogs, setAuditLogs]   = useState<CompanyAuditLog[]>([]);
  const [auditQ, setAuditQ]         = useState('');
  const [auditOpen, setAuditOpen]   = useState<string | null>(null);
  const loadAudit = (q: string) => { if (id) getCompanyAuditLogs(id, { limit: 100, q }).then(setAuditLogs).catch(() => setAuditLogs([])); };

  // ユーザー完全削除
  const [allUsers, setAllUsers]       = useState<CompanyUser[] | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [purgingId, setPurgingId]     = useState<string | null>(null);

  // メモ・営業情報
  const [metaEdit, setMetaEdit]     = useState(false);
  const [metaForm, setMetaForm]     = useState<MetaForm>({ memo: '', sales_contact: '', contract_date: '', renewal_date: '', website_url: '' });
  const [metaSaving, setMetaSaving] = useState(false);
  const [metaError, setMetaError]   = useState('');

  // 運営管理者として開く
  const [canAccess, setCanAccess]   = useState(false);
  const [opening, setOpening]       = useState(false);
  const [openingCrew, setOpeningCrew] = useState(false);

  useEffect(() => {
    if (!id) return;
    getCompany(id)
      .then(c => {
        setCompany(c); setEditName(c.name); setEditSub(c.subdomain);
        setMetaForm({ memo: c.memo ?? '', sales_contact: c.sales_contact ?? '', contract_date: c.contract_date ?? '', renewal_date: c.renewal_date ?? '', website_url: c.website_url ?? '' });
      })
      .catch(e => setError(e instanceof Error ? e.message : 'エラーが発生しました'))
      .finally(() => setLoading(false));
    getCompanyActivity(id, 30).then(setActivity).catch(() => {});
    getCompanyAuditLogs(id, { limit: 100 }).then(setAuditLogs).catch(() => {});
    getMyCompanyAccess().then(a => setCanAccess(a.all_access || a.company_ids.includes(id))).catch(() => {});
    listAssets(id).then(setLentAssets).catch(() => {});
    listOfflineTerminals(id).then(setTerminals).catch(() => {});
  }, [id]);

  // ── 運営管理者として管理画面を開く ──
  async function handleOpenAsSuperAdmin() {
    if (!id) return;
    setOpening(true);
    try {
      const { access_token, subdomain } = await accessCompanyAsSuperAdmin(id);
      openCompanyAdminConsole(subdomain, access_token);
    } catch (e) { alert(e instanceof Error ? e.message : 'アクセスに失敗しました'); }
    finally { setOpening(false); }
  }

  /** 会社の船員アプリを開く（運営アカウントは打刻不可。画面確認用） */
  async function handleOpenCrewApp() {
    if (!id) return;
    setOpeningCrew(true);
    try {
      const { access_token, subdomain } = await accessCompanyAsSuperAdmin(id);
      openCompanyCrewApp(subdomain, access_token);
    } catch (e) { alert(e instanceof Error ? e.message : 'アクセスに失敗しました'); }
    finally { setOpeningCrew(false); }
  }

  // ── 会社情報保存 ──
  async function handleSave() {
    if (!id || !company) return;
    setSaving(true); setSaveError('');
    try {
      const updated = await updateCompany(id, editName.trim(), editSub.trim());
      setCompany({ ...company, name: updated.name, subdomain: updated.subdomain });
      setEditing(false);
    } catch (e) { setSaveError(e instanceof Error ? e.message : '保存に失敗しました'); }
    finally { setSaving(false); }
  }

  // ── 管理者追加 ──
  async function handleAddAdmin() {
    if (!id) return;
    if (!addForm.user_name || !addForm.employee_code || !addForm.password) {
      setAddError('氏名・社員番号・パスワードは必須です。'); return;
    }
    setAdding(true); setAddError('');
    try {
      const created = await createAdminUser(id, addForm);
      const newAdmin: Admin = {
        id: created.id,
        user_name: addForm.user_name,
        employee_code: addForm.employee_code,
        email: addForm.email,
        user_role: 'shore_admin',
        is_active: 1,
        last_login_at: null,
      };
      setCompany(prev => prev ? { ...prev, admins: [...prev.admins, newAdmin] } : prev);
      setAddForm({ user_name: '', employee_code: '', email: '', password: '' });
      setShowAdd(false);
    } catch (e) { setAddError(e instanceof Error ? e.message : '追加に失敗しました'); }
    finally { setAdding(false); }
  }

  // ── メモ・営業情報保存 ──
  async function handleMetaSave() {
    if (!id) return;
    setMetaSaving(true); setMetaError('');
    try {
      await updateCompanyMeta(id, metaForm);
      setCompany(prev => prev ? { ...prev, ...metaForm, memo: metaForm.memo || null, sales_contact: metaForm.sales_contact || null, contract_date: metaForm.contract_date || null, renewal_date: metaForm.renewal_date || null, website_url: metaForm.website_url || null } : prev);
      setMetaEdit(false);
    } catch (e) { setMetaError(e instanceof Error ? e.message : '保存に失敗しました'); }
    finally { setMetaSaving(false); }
  }

  // ── 管理者 有効/無効切り替え ──
  async function handleToggle(admin: Admin) {
    if (!id) return;
    if (!window.confirm(`「${admin.user_name}」を${admin.is_active ? '無効化' : '有効化'}しますか？`)) return;
    setToggling(admin.id);
    try {
      const updated = await toggleAdminUser(id, admin.id);
      setCompany(prev => prev ? {
        ...prev,
        admins: prev.admins.map(a => a.id === admin.id ? { ...a, is_active: updated.is_active } : a),
      } : prev);
    } catch (e) { alert(e instanceof Error ? e.message : 'エラーが発生しました'); }
    finally { setToggling(null); }
  }

  // ── ユーザー一覧読み込み ──
  async function loadAllUsers() {
    if (!id) return;
    setLoadingUsers(true);
    try { setAllUsers(await listCompanyUsers(id)); }
    catch (e) { alert(e instanceof Error ? e.message : 'ユーザー取得に失敗しました'); }
    finally { setLoadingUsers(false); }
  }

  // ── ユーザー完全削除 ──
  async function handlePurge(u: CompanyUser) {
    if (!id) return;
    if (!window.confirm(
      `【完全削除】\n「${u.user_name}」(${u.employee_code}) をDBから完全に削除します。\n勤怠・メッセージ等すべてのデータが失われます。\n\nこの操作は取り消せません。続けますか？`
    )) return;
    setPurgingId(u.id);
    try {
      await purgeUser(id, u.id);
      setAllUsers(prev => prev ? prev.filter(x => x.id !== u.id) : prev);
    } catch (e) { alert(e instanceof Error ? e.message : '削除に失敗しました'); }
    finally { setPurgingId(null); }
  }

  // ── パスワードリセット ──
  async function handlePwReset() {
    if (!id || !pwModal) return;
    if (pwModal.value.length < 4) { setPwError('4文字以上で入力してください。'); return; }
    setPwSaving(true); setPwError('');
    try {
      await resetAdminPassword(id, pwModal.admin.id, pwModal.value);
      setPwModal(null);
    } catch (e) { setPwError(e instanceof Error ? e.message : 'エラーが発生しました'); }
    finally { setPwSaving(false); }
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 64, color: '#94a3b8' }}>読み込み中...</div>;
  if (error)   return <div style={{ background: '#fee2e2', borderRadius: 8, padding: 16, color: '#dc2626' }}>{error}</div>;
  if (!company) return null;

  return (
    <div>
      <button onClick={() => navigate('/')} style={backBtnStyle}>← 一覧に戻る</button>

      {/* 会社情報カード */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', margin: 0 }}>会社情報</h2>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {/* 運営管理者として開く（アクセス許可がある場合のみ） */}
            {canAccess && (
              <>
              <button
                onClick={handleOpenAsSuperAdmin}
                disabled={opening || !company.is_active}
                style={{ ...superAccessBtnStyle, opacity: (opening || !company.is_active) ? 0.6 : 1 }}
                title={!company.is_active ? '停止中の会社は開けません' : '運営管理者権限で管理画面を開きます'}
              >
                {opening ? '起動中...' : '⚡ 運営管理者として開く'}
              </button>
              <button
                onClick={handleOpenCrewApp}
                disabled={openingCrew || !company.is_active}
                style={{ ...superAccessBtnStyle, opacity: (openingCrew || !company.is_active) ? 0.6 : 1 }}
                title={!company.is_active ? '停止中の会社は開けません' : '船員アプリを開きます（運営アカウントでは打刻できません。船を選んで画面確認用）'}
              >
                {openingCrew ? '起動中...' : '📱 船員アプリを開く'}
              </button>
              </>
            )}
            {/* 管理画面リンク */}
            <a
              href={`https://${company.subdomain}.${ADMIN_DOMAIN}`}
              target="_blank" rel="noreferrer"
              style={adminLinkBtnStyle}
            >
              🖥️ 管理画面を開く
            </a>
            {/* APKリンク */}
            <a
              href={`${APK_BASE}/catms-crew-${company.subdomain}.apk`}
              target="_blank" rel="noreferrer"
              style={apkLinkBtnStyle}
            >
              📱 APKダウンロード
            </a>
            {/* APKビルド */}
            <button
              onClick={startApkBuild}
              disabled={apkBuild?.status === 'running'}
              style={{ ...apkLinkBtnStyle, background: '#eff6ff', color: '#1d4ed8', borderColor: '#bfdbfe', cursor: apkBuild?.status === 'running' ? 'not-allowed' : 'pointer' }}
            >
              {apkBuild?.status === 'running' ? '🔨 ビルド中...' : '🔨 APKをビルド'}
            </button>
            {/* 個人スマホアプリ配布QR */}
            <button
              onClick={() => setShowPersonalQr(true)}
              style={{ ...apkLinkBtnStyle, background: '#f0fdf4', color: '#15803d', borderColor: '#bbf7d0' }}
              title="船員の個人スマホ用アプリ（閲覧・連絡・修正依頼。打刻不可）の配布QRを表示"
            >
              📲 個人アプリQR
            </button>
            {/* 管理用アプリを開く（フル操作・動作確認/代理の検証用） */}
            <a
              href={`https://${company.subdomain}.crew.${ADMIN_DOMAIN}?app=managed`}
              target="_blank" rel="noreferrer"
              style={{ ...apkLinkBtnStyle, background: '#eef2ff', color: '#4338ca', borderColor: '#c7d2fe', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
              title="船員アプリの管理用（フル操作）。動作確認・代理操作の検証はこちらで行う"
            >
              🖥 管理用アプリを開く
            </a>
            {!editing && (
              <button onClick={() => setEditing(true)} style={editBtnStyle}>編集</button>
            )}
          </div>
        </div>

        {/* APKビルド進捗 */}
        {apkBuild && (
          <div style={{
            marginTop: 12, padding: '12px 14px', borderRadius: 10, fontSize: 12.5,
            border: `1px solid ${apkBuild.status === 'error' ? '#fecaca' : apkBuild.status === 'success' ? '#bbf7d0' : '#bfdbfe'}`,
            background: apkBuild.status === 'error' ? '#fef2f2' : apkBuild.status === 'success' ? '#f0fdf4' : '#eff6ff',
          }}>
            <div style={{ fontWeight: 700, marginBottom: 6, color: '#334155' }}>
              {apkBuild.status === 'running' && '🔨 ビルド中…（ドメイン整備＋署名APK生成。数分かかります）'}
              {apkBuild.status === 'success' && '✅ ビルド完了'}
              {apkBuild.status === 'error' && '⚠ ビルド失敗'}
            </div>
            {apkBuild.status === 'success' && (
              <a href={`${APK_BASE}/catms-crew-${company.subdomain}.apk`} target="_blank" rel="noreferrer" style={{ ...apkLinkBtnStyle, display: 'inline-block', marginBottom: 8 }}>
                📱 最新APKをダウンロード
              </a>
            )}
            {apkBuild.log?.length > 0 && (
              <pre style={{ margin: 0, maxHeight: 180, overflow: 'auto', background: '#0f172a', color: '#cbd5e1', padding: 10, borderRadius: 8, fontSize: 11, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {apkBuild.log.slice(-40).join('\n')}
              </pre>
            )}
          </div>
        )}

        {/* 個人スマホアプリ配布QRモーダル */}
        {showPersonalQr && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setShowPersonalQr(false)}>
            <div onClick={e => e.stopPropagation()}
              style={{ width: 400, maxWidth: '92vw', background: 'white', borderRadius: 16, padding: 28, textAlign: 'center' }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>📲 個人スマホ用アプリ（{company.name}）</div>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>
                船員のスマホ（<strong>iPhone・Android 共通</strong>）でQRを読み取り、開いた画面で「ホーム画面に追加」すると「カツオ勤怠」アイコンから使えます。アプリストアは不要です。<br />
                勤怠の確認・修正・連絡・ヒヤリハット報告など（<strong>打刻は船内の共有端末で</strong>）。
              </div>
              {personalQr ? (
                <img src={personalQr} alt="個人アプリQR" style={{ width: 240, height: 240, borderRadius: 8, border: '1px solid #e2e8f0' }} />
              ) : (
                <div style={{ width: 240, height: 240, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13 }}>QR生成中...</div>
              )}
              <div style={{ margin: '14px 0 16px', fontSize: 13, fontWeight: 700, color: '#0f172a', wordBreak: 'break-all' }}>
                {`https://${company.subdomain}.crew.${ADMIN_DOMAIN}/?app=personal`}
              </div>
              {personalQr && (
                <a href={personalQr} download={`カツオ勤怠_個人スマホ用アプリQR_${company.name}.png`}
                  style={{ display: 'block', margin: '-6px 0 14px', padding: '10px', borderRadius: 10, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontWeight: 700, fontSize: 13, textDecoration: 'none' }}>
                  ⬇ QR画像を保存（配布・LINE用）
                </a>
              )}
              <div style={{ margin: '-8px 0 14px', fontSize: 11.5, color: '#64748b', lineHeight: 1.6 }}>
                動作確認・代理操作の検証は<strong>管理用</strong>（<code style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: 4 }}>?app=managed</code>）で開いてください。
              </div>
              <button onClick={() => setShowPersonalQr(false)}
                style={{ width: '100%', padding: '11px', borderRadius: 10, border: 'none', background: '#0f172a', color: 'white', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                閉じる
              </button>
            </div>
          </div>
        )}

        {/* 会社ロゴ（管理サイト・船員アプリのログイン画面/ヘッダーに表示） */}
        <div style={{ marginTop: 12, padding: '14px 16px', borderRadius: 10, border: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{
            width: 160, height: 56, borderRadius: 8, background: '#fff', border: '1px dashed #cbd5e1',
            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0,
          }}>
            {company.logo_url
              ? <img src={company.logo_url} alt="会社ロゴ" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              : <span style={{ fontSize: 12, color: '#94a3b8' }}>ロゴ未設定（既定表示）</span>}
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>会社ロゴ</div>
            <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 2 }}>
              管理サイト・船員アプリのログイン画面とヘッダーに表示されます（PNG/JPG・2MBまで）
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <label style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontSize: 12.5, fontWeight: 700, cursor: logoBusy ? 'wait' : 'pointer' }}>
              {logoBusy ? '処理中…' : (company.logo_url ? 'ロゴを差し替え' : 'ロゴをアップロード')}
              <input type="file" accept="image/*" style={{ display: 'none' }} disabled={logoBusy}
                onChange={e => { const f = e.target.files?.[0]; if (f) handleLogoUpload(f); e.target.value = ''; }} />
            </label>
            {company.logo_url && (
              <button onClick={handleLogoDelete} disabled={logoBusy}
                style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #fecaca', background: '#fff', color: '#dc2626', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                削除
              </button>
            )}
          </div>
        </div>

        {saveError && <div style={errorBoxStyle}>{saveError}</div>}

        <div style={{ display: 'grid', gap: 16 }}>
          <div>
            <label style={labelStyle}>会社名</label>
            {editing ? (
              <input value={editName} onChange={e => setEditName(e.target.value)} style={inputStyle} placeholder="例: 株式会社○○海運" />
            ) : (
              <div style={valueStyle}>{company.name}</div>
            )}
          </div>
          <div>
            <label style={labelStyle}>サブドメイン</label>
            {editing ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input value={editSub} onChange={e => setEditSub(e.target.value.toLowerCase())} style={{ ...inputStyle, width: 200 }} placeholder="例: unison-tech" />
                <span style={{ fontSize: 13, color: '#64748b' }}>.unison-sea.com</span>
              </div>
            ) : (
              <div style={valueStyle}>{company.subdomain}.unison-sea.com</div>
            )}
          </div>
          <div>
            <label style={labelStyle}>登録日</label>
            <div style={valueStyle}>{fmtDateJst(company.created_at)}</div>
          </div>
          <div>
            <label style={labelStyle}>ステータス</label>
            <span style={company.is_active ? activeBadge : inactiveBadge}>
              {company.is_active ? '稼働中' : '停止中'}
            </span>
          </div>
        </div>

        {editing && (
          <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
            <button onClick={handleSave} disabled={saving} style={saveBtnStyle}>{saving ? '保存中...' : '保存する'}</button>
            <button onClick={() => { setEditName(company.name); setEditSub(company.subdomain); setSaveError(''); setEditing(false); }} disabled={saving} style={cancelBtnStyle}>キャンセル</button>
          </div>
        )}
      </div>

      {/* 管理者アカウント */}
      <div style={{ ...cardStyle, marginTop: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: 0 }}>管理者アカウント</h2>
          <button onClick={() => { setShowAdd(p => !p); setAddError(''); }} style={addAdminBtnStyle}>
            {showAdd ? '✕ キャンセル' : '＋ 管理者を追加'}
          </button>
        </div>

        {/* 追加フォーム */}
        {showAdd && (
          <div style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: 18, marginBottom: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 12 }}>新規管理者を追加</div>
            {addError && <div style={{ ...errorBoxStyle, marginBottom: 12 }}>{addError}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>氏名 *</label>
                <input value={addForm.user_name} onChange={e => setAddForm(p => ({ ...p, user_name: e.target.value }))} style={inputStyle} placeholder="例: 山田 太郎" />
              </div>
              <div>
                <label style={labelStyle}>社員番号 *</label>
                <input value={addForm.employee_code} onChange={e => setAddForm(p => ({ ...p, employee_code: e.target.value }))} style={inputStyle} placeholder="例: admin01" />
              </div>
              <div>
                <label style={labelStyle}>メールアドレス</label>
                <input value={addForm.email} onChange={e => setAddForm(p => ({ ...p, email: e.target.value }))} style={inputStyle} placeholder="例: yamada@example.com" type="email" name="new-admin-email" autoComplete="off" />
              </div>
              <div>
                <label style={labelStyle}>パスワード *</label>
                <PasswordInput value={addForm.password} onChange={e => setAddForm(p => ({ ...p, password: e.target.value }))} style={inputStyle} placeholder="初期パスワード" name="new-admin-password" autoComplete="new-password" />
              </div>
            </div>
            <button onClick={handleAddAdmin} disabled={adding} style={{ ...saveBtnStyle, marginTop: 14 }}>
              {adding ? '追加中...' : '管理者を追加する'}
            </button>
          </div>
        )}

        {company.admins.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: 14, padding: '16px 0' }}>管理者が登録されていません</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                {['氏名', '社員番号', 'メール', '役割', '最終ログイン', '状態', '操作'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: '#64748b', fontWeight: 600, fontSize: 12 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {company.admins.map(a => (
                <tr key={a.id} style={{ borderBottom: '1px solid #f1f5f9', opacity: a.is_active ? 1 : 0.55 }}>
                  <td style={tdStyle}>{a.user_name}</td>
                  <td style={tdStyle}><code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>{a.employee_code}</code></td>
                  <td style={tdStyle}>{a.email || '—'}</td>
                  <td style={tdStyle}><span style={roleBadge(a.user_role)}>{roleLabel(a.user_role)}</span></td>
                  <td style={tdStyle}>{fmtDateTimeJst(a.last_login_at, '未ログイン')}</td>
                  <td style={tdStyle}>
                    <span style={a.is_active ? userActiveBadge : userInactiveBadge}>{a.is_active ? '有効' : '無効'}</span>
                  </td>
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => handleToggle(a)}
                      disabled={toggling === a.id}
                      style={a.is_active ? disableBtnStyle : enableBtnStyle}
                    >
                      {toggling === a.id ? '…' : a.is_active ? '無効化' : '有効化'}
                    </button>
                    <button
                      onClick={() => { setPwModal({ admin: a, value: '' }); setPwError(''); }}
                      style={pwResetBtnStyle}
                    >
                      PW変更
                    </button>
                    {a.mfa_enabled ? (
                      <button
                        onClick={() => handleMfaReset(a)}
                        disabled={mfaResetting === a.id}
                        style={pwResetBtnStyle}
                        title="認証アプリ紛失時にMFAを解除します"
                      >
                        {mfaResetting === a.id ? '…' : 'MFA解除'}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ユーザー完全削除 */}
      <div style={{ ...cardStyle, marginTop: 20, border: '1.5px solid #fee2e2' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#dc2626', margin: 0 }}>ユーザー完全削除</h2>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>無効化済みユーザーをDBから完全に消去します。この操作は取り消せません。</div>
          </div>
          <button onClick={loadAllUsers} disabled={loadingUsers} style={editBtnStyle}>
            {loadingUsers ? '読込中...' : allUsers === null ? 'ユーザー一覧を表示' : '再読み込み'}
          </button>
        </div>
        {allUsers !== null && (() => {
          const inactive = allUsers.filter(u => u.is_active === 0);
          if (inactive.length === 0) return (
            <div style={{ color: '#94a3b8', fontSize: 14, padding: '12px 0' }}>無効化済みユーザーはいません</div>
          );
          const ROLE_LABELS: Record<string, string> = { crew: '船員', captain: '船長', shore_admin: '陸上管理者', system_admin: 'システム管理者' };
          return (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #fee2e2' }}>
                  {['氏名', '社員コード', '役割', '登録日', '操作'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: '#64748b', fontWeight: 600, fontSize: 12 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {inactive.map(u => (
                  <tr key={u.id} style={{ borderBottom: '1px solid #fff1f2' }}>
                    <td style={tdStyle}>{u.user_name}</td>
                    <td style={tdStyle}><code style={{ background: '#fef2f2', color: '#dc2626', padding: '2px 6px', borderRadius: 4 }}>{u.employee_code}</code></td>
                    <td style={tdStyle}><span style={{ background: '#f1f5f9', color: '#64748b', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99 }}>{ROLE_LABELS[u.user_role] ?? u.user_role}</span></td>
                    <td style={tdStyle}>{fmtDateJst(u.created_at)}</td>
                    <td style={tdStyle}>
                      <button
                        onClick={() => handlePurge(u)}
                        disabled={purgingId === u.id}
                        style={purgeBtnStyle}
                      >
                        {purgingId === u.id ? '削除中...' : '完全削除'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          );
        })()}
      </div>

      {/* 貸与端末（資材） */}
      <div style={{ ...cardStyle, marginTop: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', flex: 1 }}>📦 貸与端末（{lentAssets.length}台）</div>
          <button onClick={() => navigate('/assets')}
            style={{ fontSize: 12, fontWeight: 700, color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 7, padding: '5px 12px', cursor: 'pointer' }}>
            資材管理で編集
          </button>
        </div>
        {lentAssets.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '14px 0', color: '#94a3b8', fontSize: 13 }}>この会社に貸与中の資材はありません</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                {['管理番号', '種別', '機種', '状態', '利用状況', '貸与日'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '7px 10px', fontSize: 11.5, color: '#64748b', fontWeight: 700, borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lentAssets.map(a => (
                <tr key={a.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '8px 10px', fontWeight: 700, color: '#111827' }}>{a.asset_no}</td>
                  <td style={{ padding: '8px 10px', color: '#4b5563' }}>{({ tablet: 'タブレット', smartphone: 'スマホ', pc: 'PC', other: 'その他' } as Record<string, string>)[a.asset_type] ?? a.asset_type}</td>
                  <td style={{ padding: '8px 10px', color: '#4b5563' }}>{a.model ?? '-'}</td>
                  <td style={{ padding: '8px 10px' }}>
                    <span style={{ fontSize: 11, fontWeight: 800, borderRadius: 6, padding: '2px 8px', background: a.status === 'lent' ? '#dbeafe' : '#fef3c7', color: a.status === 'lent' ? '#1d4ed8' : '#b45309' }}>
                      {({ lent: '貸与中', repair: '修理中', in_stock: '在庫', retired: '廃棄' } as Record<string, string>)[a.status] ?? a.status}
                    </span>
                  </td>
                  <td style={{ padding: '8px 10px', color: '#4b5563', fontSize: 12 }}>
                    {a.usage?.kind === 'vessel_device'
                      ? `⚓ ${a.usage.vessel_name ?? '-'} / ${a.usage.device_name ?? '-'}（最終通信 ${a.usage.last_seen ? a.usage.last_seen.replace('T', ' ').slice(0, 16) : '-'}）`
                      : a.usage?.kind === 'mdm'
                        ? `📱 キオスク端末（${a.usage.applied_mode === 'locked' ? '制限中' : '開放'}）`
                        : '-'}
                  </td>
                  <td style={{ padding: '8px 10px', color: '#6b7280' }}>{a.lent_at ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 共有端末トークン（オフライン打刻端末の認証） */}
      <div style={{ ...cardStyle, marginTop: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>🔑 共有端末トークン（オフライン打刻端末）</div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12, lineHeight: 1.7 }}>
          船員の共有端末（オフライン打刻・顔認証ログイン用）に設定するトークンです。制作会社が発行し、端末に登録します。<br />
          設定方法: 端末ブラウザで <code style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: 4 }}>{'https://<会社>.crew.unison-sea.com/?offline_token=（発行値）'}</code> を一度開くと保存されます。
        </div>

        {issuedToken && (
          <div style={{ background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 10, padding: '12px 14px', marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#047857', marginBottom: 6 }}>✅ 「{issuedToken.label}」のトークンを発行しました（この画面を閉じると二度と表示されません。今すぐ控えてください）</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <code style={{ flex: 1, background: '#fff', border: '1px solid #d1fae5', borderRadius: 6, padding: '8px 10px', fontSize: 12, wordBreak: 'break-all' }}>{issuedToken.token}</code>
              <button onClick={() => { navigator.clipboard?.writeText(issuedToken.token).catch(() => {}); }}
                style={{ padding: '8px 12px', background: '#059669', color: '#fff', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>コピー</button>
              <button onClick={() => setIssuedToken(null)}
                style={{ padding: '8px 12px', background: '#fff', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>閉じる</button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input value={termLabel} onChange={e => setTermLabel(e.target.value)} placeholder="端末名（例: 第一ユニゾン丸 タブレット）"
            style={{ flex: 1, border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '8px 11px', fontSize: 13, outline: 'none' }} />
          <button onClick={issueTerminal} disabled={termBusy}
            style={{ padding: '8px 16px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: termBusy ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>
            {termBusy ? '発行中...' : '＋ トークン発行'}
          </button>
        </div>

        {terminals.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '10px 0', color: '#94a3b8', fontSize: 13 }}>発行済みの端末トークンはありません</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>
              {['端末名', '発行日', '最終利用', ''].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '7px 10px', fontSize: 11.5, color: '#64748b', fontWeight: 700, borderBottom: '1px solid #e2e8f0' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {terminals.map(t => (
                <tr key={t.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '8px 10px', fontWeight: 700, color: '#111827' }}>{t.label ?? '(名称なし)'}</td>
                  <td style={{ padding: '8px 10px', color: '#6b7280' }}>{t.created_at?.slice(0, 10)}</td>
                  <td style={{ padding: '8px 10px', color: '#6b7280' }}>{t.last_used_at ? t.last_used_at.replace('T', ' ').slice(0, 16) : '未使用'}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                    <button onClick={() => revokeTerminal(t)}
                      style={{ padding: '4px 10px', fontSize: 11.5, fontWeight: 700, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', borderRadius: 6, cursor: 'pointer' }}>失効</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 活動統計グラフ */}
      <div style={{ ...cardStyle, marginTop: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 12 }}>📊 打刻数推移（過去30日）</div>
        {activity.length === 0 || activity.every(d => d.count === 0) ? (
          <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8', fontSize: 14 }}>打刻データがありません</div>
        ) : (
          <>
            <MiniBarChart data={activity} />
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 8, textAlign: 'right' }}>
              合計: {activity.reduce((s, d) => s + d.count, 0).toLocaleString()}件
            </div>
          </>
        )}
      </div>

      {/* 操作履歴（一次切り分け） */}
      <div style={{ ...cardStyle, marginTop: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>🧾 操作履歴（直近100件）</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>管理サイト・船員アプリでの登録・変更。「いつから」の問い合わせは、その前日の乗下船・役職・権限の変更を見る</div>
          <input value={auditQ} onChange={e => setAuditQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') loadAudit(auditQ); }}
            placeholder="名前・操作・内容で絞り込み（Enter）" style={{ marginLeft: 'auto', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12.5, minWidth: 240 }} />
          <button onClick={() => loadAudit(auditQ)} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>検索</button>
        </div>
        {auditLogs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '16px 0', color: '#94a3b8', fontSize: 13 }}>操作履歴はまだありません</div>
        ) : (
          <div style={{ maxHeight: 420, overflow: 'auto', border: '1px solid #f1f5f9', borderRadius: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead><tr style={{ background: '#f8fafc', color: '#64748b', fontSize: 11.5 }}>
                <th style={{ padding: '8px 10px', textAlign: 'left', whiteSpace: 'nowrap' }}>日時</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', whiteSpace: 'nowrap' }}>誰が</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', whiteSpace: 'nowrap' }}>どこから</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>操作</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>内容</th>
              </tr></thead>
              <tbody>
                {auditLogs.map(l => {
                  const ok = (l.status ?? 0) < 400; const isOpen = auditOpen === l.id;
                  return (
                    <tr key={l.id} style={{ borderTop: '1px solid #f1f5f9', cursor: 'pointer', background: ok ? undefined : '#fff7f7' }} onClick={() => setAuditOpen(isOpen ? null : l.id)}>
                      <td style={{ padding: '7px 10px', whiteSpace: 'nowrap', color: '#334155' }}>{fmtDateTimeJst(l.created_at)}</td>
                      <td style={{ padding: '7px 10px', whiteSpace: 'nowrap', fontWeight: 700 }}>{l.user_name ?? '—'}<span style={{ marginLeft: 5, fontSize: 11, color: '#64748b', fontWeight: 500 }}>{l.user_role ?? ''}</span></td>
                      <td style={{ padding: '7px 10px', whiteSpace: 'nowrap', color: '#475569' }}>{l.app_type === 'admin' ? '管理サイト' : l.app_type === 'crew' ? '船員アプリ' : (l.app_type ?? '—')}</td>
                      <td style={{ padding: '7px 10px', fontWeight: 700, color: ok ? '#1e293b' : '#b91c1c' }}>{l.action}{!ok && ` (失敗 ${l.status})`}</td>
                      <td style={{ padding: '7px 10px', color: '#475569', maxWidth: 420 }}>
                        <div style={{ whiteSpace: isOpen ? 'pre-wrap' : 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: isOpen ? 'monospace' : undefined, fontSize: isOpen ? 11.5 : undefined }}>{l.summary ?? '—'}</div>
                        {isOpen && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>{l.method} {l.path}{l.ip ? ` / ${l.ip}` : ''}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* アラート設定（アルコール閾値・残業・最大労働時間）は各社の運用設定のため
          会社の管理コンソール側に一本化。運営管理からは表示しない。 */}

      {/* メモ・営業情報 */}
      <div style={{ ...cardStyle, marginTop: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: 0 }}>メモ・営業情報</h2>
          {!metaEdit && (
            <button onClick={() => setMetaEdit(true)} style={editBtnStyle}>編集</button>
          )}
        </div>
        {metaError && <div style={{ ...errorBoxStyle, marginBottom: 14 }}>{metaError}</div>}
        {metaEdit ? (
          <div style={{ display: 'grid', gap: 14 }}>
            <div>
              <label style={labelStyle}>社内メモ</label>
              <textarea value={metaForm.memo} onChange={e => setMetaForm(p => ({ ...p, memo: e.target.value }))}
                rows={4} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                placeholder="契約状況・注意事項など自由記入" />
            </div>
            <div>
              <label style={labelStyle}>担当営業・連絡先</label>
              <input value={metaForm.sales_contact} onChange={e => setMetaForm(p => ({ ...p, sales_contact: e.target.value }))}
                style={inputStyle} placeholder="例: 田中 一郎 (090-xxxx-xxxx)" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>契約日</label>
                <input type="date" value={metaForm.contract_date} onChange={e => setMetaForm(p => ({ ...p, contract_date: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>更新日</label>
                <input type="date" value={metaForm.renewal_date} onChange={e => setMetaForm(p => ({ ...p, renewal_date: e.target.value }))} style={inputStyle} />
              </div>
            </div>
            <div>
              <label style={labelStyle}>会社HP（URL）</label>
              <input type="url" value={metaForm.website_url} onChange={e => setMetaForm(p => ({ ...p, website_url: e.target.value }))} placeholder="https://example.co.jp" style={inputStyle} />
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>管理コンソールの会社情報に表示される会社ホームページのリンク</div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <button onClick={handleMetaSave} disabled={metaSaving} style={saveBtnStyle}>{metaSaving ? '保存中...' : '保存する'}</button>
              <button onClick={() => { setMetaEdit(false); setMetaError(''); }} disabled={metaSaving} style={cancelBtnStyle}>キャンセル</button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <label style={labelStyle}>社内メモ</label>
              <div style={{ ...valueStyle, whiteSpace: 'pre-wrap', fontSize: 14, color: company.memo ? '#1e293b' : '#94a3b8' }}>{company.memo || '—'}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>担当営業・連絡先</label>
                <div style={valueStyle}>{company.sales_contact || '—'}</div>
              </div>
              <div>
                <label style={labelStyle}>契約日</label>
                <div style={valueStyle}>{company.contract_date || '—'}</div>
              </div>
              <div>
                <label style={labelStyle}>更新日</label>
                <div style={valueStyle}>{company.renewal_date || '—'}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* パスワードリセットモーダル */}
      {pwModal && (
        <div style={overlayStyle} onClick={() => setPwModal(null)}>
          <div style={modalStyle} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>パスワード変更</div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 18 }}>{pwModal.admin.user_name}（{pwModal.admin.employee_code}）</div>
            {pwError && <div style={{ ...errorBoxStyle, marginBottom: 14 }}>{pwError}</div>}
            <label style={labelStyle}>新しいパスワード（4文字以上）</label>
            <PasswordInput
              name="reset-admin-password"
              autoComplete="new-password"
              value={pwModal.value}
              onChange={e => setPwModal(p => p ? { ...p, value: e.target.value } : p)}
              style={{ ...inputStyle, marginBottom: 18 }}
              placeholder="新しいパスワード"
              autoFocus
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handlePwReset} disabled={pwSaving} style={saveBtnStyle}>
                {pwSaving ? '変更中...' : '変更する'}
              </button>
              <button onClick={() => setPwModal(null)} style={cancelBtnStyle}>キャンセル</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function roleLabel(r: string) {
  return r === 'system_admin' ? 'システム管理者' : r === 'shore_admin' ? '陸上管理者' : r;
}
function roleBadge(r: string): React.CSSProperties {
  const isSystem = r === 'system_admin';
  return { background: isSystem ? '#dbeafe' : '#dcfce7', color: isSystem ? '#1d4ed8' : '#16a34a', fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 99 };
}

const backBtnStyle: React.CSSProperties = { background: 'none', border: 'none', color: '#3b82f6', fontSize: 14, cursor: 'pointer', padding: '0 0 16px', fontWeight: 500 };
const cardStyle: React.CSSProperties = { background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' };
const editBtnStyle: React.CSSProperties = { background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const saveBtnStyle: React.CSSProperties = { background: '#1e40af', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const cancelBtnStyle: React.CSSProperties = { background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, padding: '10px 16px', fontSize: 14, cursor: 'pointer' };
const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6 };
const valueStyle: React.CSSProperties = { fontSize: 15, color: '#1e293b', fontWeight: 500 };
const inputStyle: React.CSSProperties = { width: '100%', border: '1.5px solid #cbd5e1', borderRadius: 8, padding: '9px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box' };
const tdStyle: React.CSSProperties = { padding: '10px 10px', color: '#374151' };
const errorBoxStyle: React.CSSProperties = { background: '#fee2e2', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13 };
const addAdminBtnStyle: React.CSSProperties = { background: '#ecfdf5', color: '#059669', border: '1.5px solid #a7f3d0', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const disableBtnStyle: React.CSSProperties = { background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginRight: 6 };
const enableBtnStyle: React.CSSProperties = { background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginRight: 6 };
const pwResetBtnStyle: React.CSSProperties = { background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' };
const activeBadge: React.CSSProperties = { background: '#dbeafe', color: '#1d4ed8', fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 99, display: 'inline-block' };
const inactiveBadge: React.CSSProperties = { background: '#fee2e2', color: '#dc2626', fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 99, display: 'inline-block' };
const userActiveBadge: React.CSSProperties = { background: '#dcfce7', color: '#16a34a', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99 };
const userInactiveBadge: React.CSSProperties = { background: '#f1f5f9', color: '#94a3b8', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99 };
const adminLinkBtnStyle: React.CSSProperties = { background: '#eff6ff', color: '#1d4ed8', border: '1.5px solid #bfdbfe', borderRadius: 8, padding: '7px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', textDecoration: 'none' };
const superAccessBtnStyle: React.CSSProperties = { background: 'linear-gradient(135deg, #7c3aed, #4f46e5)', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const apkLinkBtnStyle: React.CSSProperties = { background: '#f0fdf4', color: '#15803d', border: '1.5px solid #bbf7d0', borderRadius: 8, padding: '7px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', textDecoration: 'none' };
const purgeBtnStyle: React.CSSProperties = { background: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' };
const overlayStyle: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 };
const modalStyle: React.CSSProperties = { background: '#fff', borderRadius: 14, padding: 28, maxWidth: 420, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' };
